import React, { useState, useMemo, useEffect } from 'react';
import { Alert, Button, Card, Row, Col, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Loading } from '@rd-web-markets/shared/dist/util';
import ClaimStateAlerts from '@rd-web-markets/shared/dist/claim/ClaimStateAlerts';
import claimConsumablesService from '../../services/claim/claim_consumables.service';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { helpPages } from './Help';
import TeachingBubble from '@rd-web-markets/shared/dist/util/TeachingBubble';
import { ConsumablesHeader, ConsumablesRow, ConsumablesTotalsRow } from './';
import { createTimezonedDate } from '@rd-web-markets/shared/dist/util/date/dateUtils';
import { useSelector } from 'react-redux';
import NotesList from '@rd-web-markets/shared/dist/financialSection/NotesList';
import CostFragmentsTable from '../costFragments/CostFragmentsTable';
import ReactDataGridCostFragmentsTable from '../costFragments/ReactDataGridCostFragmentsTable';
import AddNoteButton from '../AddNoteButton';
export const ConsumablesSection = _ref => {
  let {
    accountType,
    claim,
    refreshClaim,
    setClaim,
    totalPayrollCostInvalid,
    showCostFragmentsTable,
    allProjects
  } = _ref;
  const [claimConsumables, setClaimConsumables] = useState(null);
  const [headerScrollPosition, setHeaderScrollPosition] = useState(0);
  const dispatch = useDispatch();
  const claimId = useMemo(() => claim?.id, [claim]);
  const user = useSelector(state => state.auth.user);
  const [isShowInvolvement, setIsShowInvolvement] = useState(null);
  useEffect(() => {
    async function getData() {
      try {
        if (claimId) {
          setClaimConsumables(await claimConsumablesService.all(claimId));
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    }
    getData();
    setIsShowInvolvement(claim.claim_group.cost_methodology === 'consolidated');
  }, [dispatch, claimId]);
  const handleDelete = async index => {
    const consumable = claimConsumables[index];
    if (consumable.id) {
      try {
        await claimConsumablesService.delete(consumable);
        const consumables = await claimConsumablesService.all(claim.id);
        setClaimConsumables(consumables);
        refreshClaim();
      } catch (error) {
        dispatch(handleError(error));
      }
    } else {
      const newConsumables = [...claimConsumables];
      newConsumables.splice(index, 1);
      setClaimConsumables(newConsumables);
    }
  };
  const handleAdd = consumable => {
    setClaimConsumables([...claimConsumables, consumable]);
  };
  const hasPermissions = () => {
    return user.account_type !== 'customer' && user.account_type !== 'accountant';
  };
  const userUnauthorizedToEdit = !hasPermissions();
  const sortedClaimProjectReports = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.sort((a, b) => {
      if (a.project_name < b.project_name) {
        return -1;
      }
      if (a.project_name > b.project_name) {
        return 1;
      }
      return 0;
    });
  }, [claim]);
  if (!claim || !claimConsumables) return /*#__PURE__*/React.createElement(Loading, null);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ClaimStateAlerts, {
    claim: claim
  }), /*#__PURE__*/React.createElement(Alert, {
    variant: "info"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "info"), " Please ensure that:", /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", null, "all costs were incurred between ", createTimezonedDate(claim.from_date).toCustomLocaleDateString(), " and ", createTimezonedDate(claim.to_date).toCustomLocaleDateString()), /*#__PURE__*/React.createElement("li", null, "total Utilities cost amount matches the financial accounts P&L"))), totalPayrollCostInvalid ? /*#__PURE__*/React.createElement(Alert, {
    variant: "warning"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "warning"), " Total payroll cost is either empty or smaller than the sum of employee costs. Please go to the Staffing Costs tab to correct this.") : null, /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end align-items-center gap-2 mb-3"
  }, /*#__PURE__*/React.createElement(AddNoteButton, {
    claimId: claim.id,
    category: "consumables",
    userCanEdit: hasPermissions() && !claim.financials_locked && !claim.is_locked
  }), /*#__PURE__*/React.createElement(TeachingBubble, {
    pages: helpPages,
    position: "left"
  })), claimConsumables.filter(c => c.item_type === 'Consumables' && c.group_as_various).length === 1 ? /*#__PURE__*/React.createElement(Alert, {
    variant: "warning"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "warning"), " If only a single utility is grouped as various it wont be grouped as various in the report table.") : null, !showCostFragmentsTable && /*#__PURE__*/React.createElement(React.Fragment, null, claimConsumables.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Table, {
    size: "sm",
    className: "pt-4"
  }, /*#__PURE__*/React.createElement("thead", {
    className: "text-center"
  }, /*#__PURE__*/React.createElement(ConsumablesHeader, {
    claim: claim,
    setHeaderScrollPosition: setHeaderScrollPosition,
    isShowInvolvement: isShowInvolvement,
    allProjects: allProjects
  })), /*#__PURE__*/React.createElement("tbody", null, claimConsumables.map((consumable, index) => {
    return /*#__PURE__*/React.createElement(ConsumablesRow, {
      key: index,
      headerScrollPosition: headerScrollPosition,
      sortedClaimProjectReports: sortedClaimProjectReports,
      index: index,
      claim: claim,
      handleDelete: () => handleDelete(index),
      claimConsumables: claimConsumables,
      setClaimConsumables: setClaimConsumables,
      refreshClaim: refreshClaim,
      totalPayrollCostInvalid: totalPayrollCostInvalid,
      handleError: handleError,
      claimConsumablesService: claimConsumablesService,
      isShowInvolvement: isShowInvolvement
    });
  }), /*#__PURE__*/React.createElement(ConsumablesTotalsRow, {
    claim: claim,
    isShowInvolvement: isShowInvolvement
  })))) : /*#__PURE__*/React.createElement(Row, {
    className: "pt-5 pb-5 text-center"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement("h3", {
    className: "text-center text-muted"
  }, "No data"))), hasPermissions() && /*#__PURE__*/React.createElement(Row, {
    className: "mt-4"
  }, /*#__PURE__*/React.createElement(Col, null, /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    onClick: () => handleAdd({
      claim_id: claim.id,
      item_type: 'Consumables',
      claim_employee_rnd_apportionments: []
    }),
    disabled: claim.is_locked || claim.financials_locked || claim.active_change_set
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "add"), " Add Row")))), showCostFragmentsTable && /*#__PURE__*/React.createElement(ReactDataGridCostFragmentsTable, {
    claim: claim,
    refreshClaim: refreshClaim,
    costFragmentType: "UkSection::ClaimConsumable",
    userUnauthorizedToEdit: userUnauthorizedToEdit,
    allProjects: allProjects
  }), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-start"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "outline-primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/external_workers`
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_left"), " Back")), /*#__PURE__*/React.createElement(Col, {
    md: 6,
    className: "text-end"
  }, /*#__PURE__*/React.createElement(Button, {
    as: Link,
    variant: "primary",
    to: `/${accountType}/claims/${claim.id}/financial_details/software`
  }, "Continue ", /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18"
  }, "chevron_right")))));
};